import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSave, FaArrowLeft } from 'react-icons/fa';
import creditCardService from '../services/creditCardService';

const CreditCardForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    number: '',
    closingDay: '',
    dueDay: '',
    status: 'ACTIVE'
  });
  const [errors, setErrors] = useState({});

  const loadCreditCard = useCallback(async (creditCardId) => {
    try {
      setLoading(true);
      const data = await creditCardService.getById(creditCardId);
      setFormData({
        name: data.name,
        brand: data.brand,
        number: data.number,
        closingDay: data.closingDay,
        dueDay: data.dueDay,
        status: data.status
      });
    } catch (error) {
      console.error('Erro ao carregar cartão de crédito:', error);
      toast.error('Erro ao carregar dados do cartão de crédito. Tente novamente.');
      navigate('/credit-cards');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (id) {
      loadCreditCard(id);
    }
  }, [id, loadCreditCard]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let newValue = value;
    
    if (name === 'number' && value !== '') {
      // Aceitar apenas números
      newValue = value.replace(/\D/g, '');
    } else if ((name === 'closingDay' || name === 'dueDay') && value !== '') {
      // Aceitar apenas números e limitar a 31
      newValue = value.replace(/\D/g, '');
      if (parseInt(newValue) > 31) {
        newValue = '31';
      }
    }
    
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    
    // Limpar erro quando o campo for preenchido
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'O nome é obrigatório';
    }
    if (!formData.brand) {
      newErrors.brand = 'A bandeira é obrigatória';
    }
    if (formData.number && !/^\d{4}$/.test(formData.number)) {
      newErrors.number = 'O número deve conter exatamente 4 dígitos';
    }
    if (formData.closingDay && (isNaN(formData.closingDay) || formData.closingDay < 1 || formData.closingDay > 31)) {
      newErrors.closingDay = 'O dia de fechamento deve estar entre 1 e 31';
    }
    if (formData.dueDay && (isNaN(formData.dueDay) || formData.dueDay < 1 || formData.dueDay > 31)) {
      newErrors.dueDay = 'O dia de vencimento deve estar entre 1 e 31';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const creditCardData = {
        name: formData.name,
        brand: formData.brand,
        number: formData.number || null,
        closingDay: formData.closingDay ? parseInt(formData.closingDay) : null,
        dueDay: formData.dueDay ? parseInt(formData.dueDay) : null,
        status: formData.status || 'ACTIVE'
      };
      
      if (id) {
        await creditCardService.update(id, creditCardData);
        toast.success('Cartão de crédito atualizado com sucesso!');
      } else {
        await creditCardService.create(creditCardData);
        toast.success('Cartão de crédito criado com sucesso!');
      }
      
      navigate('/credit-cards');
    } catch (error) {
      console.error('Erro ao salvar cartão de crédito:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar cartão de crédito. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <div className="sheet-container">
        <div className="sheet-header">
          <h5 className="sheet-title">{id ? 'Editar Cartão de Crédito' : 'Novo Cartão de Crédito'}</h5>
          <div className="sheet-toolbar">
            <Button 
              variant="outline-secondary"
              onClick={() => navigate('/credit-cards')}
            >
              <FaArrowLeft className="me-1" /> Voltar
            </Button>
          </div>
        </div>
        
        <div className="sheet-content">
          <Form onSubmit={handleSubmit} className="excel-form" data-testid="credit-card-form">
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="cardName">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control
                    type="text"
                    name="name" data-testid="credit-card-name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.name ? 'is-invalid' : ''}`}
                    autoFocus
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="cardBrand">
                  <Form.Label>Bandeira</Form.Label>
                  <Form.Select
                    name="brand" data-testid="credit-card-brand"
                    value={formData.brand}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.brand ? 'is-invalid' : ''}`}
                  >
                    <option value="">Selecione...</option>
                    <option value="VISA">Visa</option>
                    <option value="MASTERCARD">Mastercard</option>
                    <option value="ELO">Elo</option>
                    <option value="AMEX">Amex</option>
                    <option value="OUTRO">Outro</option>
                  </Form.Select>
                  {errors.brand && (
                    <div className="invalid-feedback">{errors.brand}</div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="cardNumber">
                  <Form.Label>Número (últimos 4 dígitos - opcional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="number" data-testid="credit-card-number"
                    value={formData.number}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.number ? 'is-invalid' : ''}`}
                    maxLength={4}
                    placeholder="4 dígitos"
                  />
                  {errors.number && (
                    <div className="invalid-feedback">{errors.number}</div>
                  )}
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="cardStatus">
                  <Form.Label>Status (opcional)</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="excel-form-control"
                  >
                    <option value="">Selecione...</option>
                    <option value="ACTIVE">Ativo</option>
                    <option value="INACTIVE">Inativo</option>
                    <option value="CANCELLED">Cancelado</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="cardClosingDay">
                  <Form.Label>Dia de Fechamento (opcional)</Form.Label>
                  <Form.Control
                    type="number"
                    name="closingDay" data-testid="credit-card-closing-day"
                    value={formData.closingDay}
                    onChange={handleChange}
                    className="excel-form-control"
                    min={1}
                    max={31}
                    placeholder="1-31"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="cardDueDay">
                  <Form.Label>Dia de Vencimento (opcional)</Form.Label>
                  <Form.Control
                    type="number"
                    name="dueDay" data-testid="credit-card-due-day"
                    value={formData.dueDay}
                    onChange={handleChange}
                    className="excel-form-control"
                    min={1}
                    max={31}
                    placeholder="1-31"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <div className="d-flex justify-content-end mt-4">
              <Button 
                type="submit" data-testid="save-button"
                className="excel-btn" 
                disabled={loading}
              >
                <FaSave className="me-2" />
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default CreditCardForm; 